/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { type ComponentPropsWithoutRef, type FC } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Button as CpdButton, IconButton, Tooltip } from "@vector-im/compound-web";
import {
  MicOnSolidIcon,
  MicOffSolidIcon,
  VideoCallSolidIcon,
  VideoCallOffSolidIcon,
  EndCallIcon,
  ShareScreenSolidIcon,
  SettingsSolidIcon,
  ShareIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";

import styles from "./Button.module.css";

interface MicButtonProps extends ComponentPropsWithoutRef<"button"> {
  muted: boolean;
  /**
   * Буква горячей клавиши, которую показываем подсказкой.
   * Передаём только там, где сочетание действительно работает: в окне звонка
   * (CTO 14.09.2026 — «добавь буквы горячих клавиш на тултипы кнопок ВКС»).
   * В прихожей клавиши не привязаны, и подсказка там была бы обманом.
   */
  shortcut?: string;
}

export const MicButton: FC<MicButtonProps> = ({ muted, shortcut, ...props }) => {
  const { t } = useTranslation();
  const Icon = muted ? MicOffSolidIcon : MicOnSolidIcon;
  const label = muted
    ? t("unmute_microphone_button_label")
    : t("mute_microphone_button_label");

  return (
    <Tooltip label={label} caption={shortcut}>
      <CpdButton
        iconOnly
        Icon={Icon}
        kind={muted ? "primary" : "secondary"}
        aria-keyshortcuts={shortcut}
        {...props}
      />
    </Tooltip>
  );
};

interface VideoButtonProps extends ComponentPropsWithoutRef<"button"> {
  muted: boolean;
  /** Буква горячей клавиши; см. пояснение у {@link MicButtonProps.shortcut}. */
  shortcut?: string;
}

export const VideoButton: FC<VideoButtonProps> = ({
  muted,
  shortcut,
  ...props
}) => {
  const { t } = useTranslation();
  const Icon = muted ? VideoCallOffSolidIcon : VideoCallSolidIcon;
  const label = muted
    ? t("start_video_button_label")
    : t("stop_video_button_label");

  return (
    <Tooltip label={label} caption={shortcut}>
      <CpdButton
        iconOnly
        Icon={Icon}
        kind={muted ? "primary" : "secondary"}
        aria-keyshortcuts={shortcut}
        {...props}
      />
    </Tooltip>
  );
};

interface ShareScreenButtonProps extends ComponentPropsWithoutRef<"button"> {
  enabled: boolean;
}

export const ShareScreenButton: FC<ShareScreenButtonProps> = ({
  enabled,
  ...props
}) => {
  const { t } = useTranslation();
  const label = enabled
    ? t("stop_screenshare_button_label")
    : t("screenshare_button_label");

  return (
    <Tooltip label={label}>
      <CpdButton
        iconOnly
        Icon={ShareScreenSolidIcon}
        kind={enabled ? "primary" : "secondary"}
        {...props}
      />
    </Tooltip>
  );
};

interface ShareLinkButtonProps extends ComponentPropsWithoutRef<"button"> {
  variant?: "default" | "icon";
}

export const ShareLinkButton: FC<ShareLinkButtonProps> = ({
  variant = "default",
  ...props
}) => {
  const { t } = useTranslation();
  const label = t("share_call_link");

  return (
    <>
      {variant === "default" && (
        <Tooltip label={label}>
          <CpdButton
            iconOnly
            Icon={ShareIcon}
            kind="secondary"
            {...props}
          />
        </Tooltip>
      )}
      {variant === "icon" && (
        <Tooltip label={label}>
          <IconButton size="32px" {...props}>
            <ShareIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export const EndCallButton: FC<ComponentPropsWithoutRef<"button">> = ({
  className,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip label={t("hangup_button_label")}>
      <CpdButton
        className={classNames(className, styles.endCall)}
        iconOnly
        Icon={EndCallIcon}
        destructive
        {...props}
      />
    </Tooltip>
  );
};

export const SettingsButton: FC<ComponentPropsWithoutRef<"button">> = (
  props,
) => {
  const { t } = useTranslation();

  return (
    <Tooltip label={t("common.settings")}>
      <CpdButton
        iconOnly
        Icon={SettingsSolidIcon}
        kind="secondary"
        {...props}
      />
    </Tooltip>
  );
};
